import { ComponentRef, createComponent, EnvironmentInjector, inject, Injector, NgZone, OnDestroy, Service, Type } from '@angular/core';

import postscribe from 'postscribe';
import { Observable } from 'rxjs';

import { BaasAppState, BaasAuthService, checkLocalhost, checkSsl, checkUrlAbsolute, concatUrl } from '@lvflanders/bar';
import { _BaasWindow } from '@lvflanders/bar-ng';

import { NG_UI_STORAGE_KEY_PREFIX } from '../../constants';
import { BarImpersonationWidgetComponent } from '../bar-layout/bar-impersonation-widget/bar-impersonation-widget.component';
import { _BAAS_BURGERPROFIEL_CONTACT_ACTION, _BaasBurgerprofielContactActionHook } from './baas-burgerprofiel-contact-action';
import { BAAS_LAYOUT_BURGERPROFIEL, BaasLayoutBurgerprofielOptions } from './baas-burgerprofiel-feature';
import { _BaasBurgerprofielApplicativeLink, _BaasBurgerprofielContactLink, _BaasBurgerprofielServicePoints } from './types';

type BurgerprofielConfig = {
    /** Whether the header & footer can be shown (if supported). */
    headerFooterActive: boolean;
    headerFooterActiveAppCodes: string[];
    /** Whether the environment supports burgerprofiel */
    supported: boolean;
    widgetRootUrl: string;
    headerWidgetId: string;
    footerWidgetId: string;
    loginUrl: string;
    loginRedirectUrl: string;
    logoutUrl: string;
    sessionExtendTimeoutSeconds: number | undefined | null;
    useActivityTracking: boolean;
    cdnRootUrl: string;
    cdnScripts: string[];
    impersonationOverrideEnabled: boolean;
};

declare global {
    interface Window {
        barBurgerprofielEngine: _BarBurgerprofielEngine | undefined;
        vl: any;
    }
}

const STORAGE_KEY_PREFIX = NG_UI_STORAGE_KEY_PREFIX + 'burgerprofiel.';
const STORAGE_KEY_VISIBLE = STORAGE_KEY_PREFIX + 'visible';
/** Internal class to centralize burgerprofiel widgets logic. */
@Service({ autoProvided: false })
export class _BarBurgerprofielEngine implements OnDestroy {
    readonly #state = inject(BaasAppState, { optional: true });
    readonly #auth = inject(BaasAuthService, { optional: true });
    readonly #environmentInjector = inject(EnvironmentInjector);
    readonly #injector = inject(Injector);
    readonly #window = inject(_BaasWindow);
    readonly #zone = inject(NgZone);
    readonly #contactActionHooks = inject<_BaasBurgerprofielContactActionHook[]>(_BAAS_BURGERPROFIEL_CONTACT_ACTION, { optional: true });
    readonly #options = inject<BaasLayoutBurgerprofielOptions>(BAAS_LAYOUT_BURGERPROFIEL);

    #sessionInterval: number | undefined = undefined;
    #contactPanelObserver?: MutationObserver;
    #headerComponent?: Type<any>;
    #headerComponentRef: ComponentRef<any> | undefined;
    // Stores the contextual contact links with their icon information
    #contextualLinks: _BaasBurgerprofielContactLink[] = [];

    /** Whether the global header and footer are to be shown. */
    get visible() {
        const supported =
            // not for polaris
            this.#state &&
            // must be supported by the environment
            this.supported;
        // never visible if not supported (even if forced or overridden)
        if (!supported) return false;

        const visibleOverride = localStorage.getItem(STORAGE_KEY_VISIBLE);
        // if the visibility override has been defined then use this value
        if (visibleOverride != null) return visibleOverride === 'true';

        return (
            this.#options._forceBurgerprofiel ||
            // must be active in configuration
            this.#config.headerFooterActive ||
            // or application code must be active in configuration
            (this.#config.headerFooterActiveAppCodes && this.#config.headerFooterActiveAppCodes.indexOf(this.#state.toepassing.code) > -1)
        );
    }
    /** Override visibility of the burgerprofiel.    */
    set visible(value: boolean) {
        localStorage.setItem(STORAGE_KEY_VISIBLE, String(value));
    }

    /** Whether Burgerprofiel is supported in the current environment. */
    get supported() {
        // it's also supported in localhost (ng serve)
        const localhostSupported = checkLocalhost() && ((checkSsl() && window.location.port === '44307') || (!checkSsl() && window.location.port === '55601'));
        return this.#config.supported || localhostSupported;
    }

    get #vl() {
        return window.vl;
    }

    get #config(): BurgerprofielConfig {
        return (this.#state?.config?.baasStatic?.burgerprofiel as BurgerprofielConfig) || ({ headerFooterActive: false } as BurgerprofielConfig);
    }

    ngOnDestroy(): void {
        // Clean up the global reference when the service is destroyed
        if (window.barBurgerprofielEngine === this) {
            delete window.barBurgerprofielEngine;
        }

        // Clean up session interval if it exists
        if (this.#sessionInterval !== undefined) {
            window.clearInterval(this.#sessionInterval);
            this.#sessionInterval = undefined;
        }

        if (this.#contactPanelObserver) {
            this.#contactPanelObserver.disconnect();
            this.#contactPanelObserver = undefined;
        }
        this.#headerComponentRef?.destroy();
    }

    // this holds the original service points as retrieved from burgerprofiel
    #originalServicePoints: any;
    // this holds the burgerpforiel contact extension
    #contactExtension: any;
    // this holds the burgerprofiel citizen profile extension
    #citizenProfileExtension: any;
    // this queues the first assignment of service points
    #pendingServicePoints: _BaasBurgerprofielServicePoints | undefined = undefined;
    // this queues the first assignment of applicative links
    #pendingApplicativeLinks: _BaasBurgerprofielApplicativeLink[] | undefined = undefined;

    /** Initializes the widgets if possible. */
    tryInitialize(): Observable<void> {
        return new Observable<void>((subscriber) => {
            const complete = () => {
                subscriber.next();
                subscriber.complete();
            };
            if (this.visible && this.#auth) {
                const doInit = () => {
                    if (this.#vl) {
                        // Capture any widget that is present or will be present on the webpage.
                        this.#vl.widget.client.capture((widget: any) => {
                            // Make this instance globally accessible for href javascript calls (e.g. contact link)
                            window.barBurgerprofielEngine = this;

                            // Only process the widget if widget is a global header.
                            if (widget.getPluginTypeId() === 'global_header') {
                                // Get the Citizen Profile Session extension from the global header widget.
                                widget.getExtension('citizen_profile.session').then(async (session: any) => {
                                    // setup session
                                    this.#configureSession(session);
                                    // try extend session automatically
                                    await this.#tryRegisterSessionExtend(session);
                                    // do some custom processing
                                    this.#customProcessing();
                                });

                                // Get the contact extension and set a test link
                                widget.getExtension('contact').then((contact: any) => {
                                    if (!this.#contactPanelObserver) {
                                        const header = document.getElementById('vl-global-header');
                                        if (header) {
                                            this.#contactPanelObserver = new MutationObserver(() => {
                                                // check if popup appeared
                                                const panels = document.querySelectorAll('.vlw__contact-panel__layout');
                                                if (panels.length > 1) {
                                                    // keep the first one, remove the rest
                                                    panels.forEach((panel, i) => {
                                                        if (i > 0) panel.remove();
                                                    });
                                                }

                                                const title = document.querySelector('.vlw__contact-panel__subtitle');
                                                if (title && !document.querySelector('.lv-burgerprofiel-contact-header-slot') && this.#headerComponent) {
                                                    const hostElement = document.createElement('div');
                                                    hostElement.className = 'lv-burgerprofiel-contact-header-slot';
                                                    // place it immediately after the <h2>
                                                    title.insertAdjacentElement('afterend', hostElement);
                                                    if (this.#headerComponentRef) {
                                                        // destroy previous if any
                                                        this.#headerComponentRef.destroy();
                                                    }
                                                    // here we dynamicall inject the component
                                                    const componentRef = createComponent(this.#headerComponent, {
                                                        environmentInjector: this.#injector.get(EnvironmentInjector)
                                                    });
                                                    hostElement.appendChild(componentRef.location.nativeElement);
                                                    componentRef.changeDetectorRef.detectChanges();
                                                    this.#headerComponentRef = componentRef;
                                                }

                                                // Update icons in the contact panel based on contextual links
                                                this.#updateContactPanelIcons();
                                            });
                                            this.#contactPanelObserver.observe(header, { childList: true, subtree: true });
                                        } else console.warn('BurgerprofielEngine: could not find global header element.');
                                    }

                                    this.#fixUpContactToggle();

                                    this.#originalServicePoints = contact.getServicePoints();
                                    this.#contactExtension = contact;

                                    // Apply pending service points if any
                                    if (this.#pendingServicePoints) {
                                        this._setContactServicePoints(this.#pendingServicePoints);
                                        this.#pendingServicePoints = undefined;
                                    }
                                });

                                widget.getExtension('citizen_profile').then((extension: any) => {
                                    this.#citizenProfileExtension = extension;

                                    // Apply pending applicative links if any
                                    if (this.#pendingApplicativeLinks) {
                                        this._setApplicativeLinks(this.#pendingApplicativeLinks);
                                        this.#pendingApplicativeLinks = undefined;
                                    }
                                });

                                widget.on('citizen_profile.session.logout.request', async (logoutRequest: any) => {
                                    await this.#tryPerformLogout(logoutRequest);
                                });
                            }
                        });
                    }
                    complete();
                };

                if (this.#vl) doInit();
                else {
                    let cdnScripts: string[];

                    if (this.#config.cdnScripts) {
                        if (!this.#config.cdnRootUrl) cdnScripts = this.#config.cdnScripts;
                        else {
                            cdnScripts = this.#config.cdnScripts.map((cdnScript) => {
                                if (checkUrlAbsolute(cdnScript)) return cdnScript;
                                return concatUrl(this.#config.cdnRootUrl, cdnScript);
                            });
                        }
                    } else {
                        cdnScripts = [
                            'https://prod.widgets.burgerprofiel.vlaanderen.be/api/v1/node_modules/@govflanders/vl-widget-polyfill/dist/index.js',
                            'https://prod.widgets.burgerprofiel.vlaanderen.be/api/v1/node_modules/@govflanders/vl-widget-client/dist/index.js'
                        ];
                    }
                    let toInject = '';
                    cdnScripts.forEach((cdnScript) => {
                        const scriptTag = `<script src="${cdnScript}"></script>`;
                        toInject += scriptTag;
                    });
                    // load third party deps
                    postscribe(document.body, toInject, {
                        done: () => doInit()
                    });
                }
            } else complete();
        });
    }
    /** Mounts the header if possible. */
    tryMountHeader(element: HTMLElement): void {
        if (this.#vl && this.#config.widgetRootUrl && this.#config.headerWidgetId) {
            let url = this.#config.widgetRootUrl;
            if (!url.endsWith('/')) url += '/';
            url += this.#config.headerWidgetId;

            this.#zone.runOutsideAngular(() => {
                this.#vl.widget.client
                    .bootstrap(url)
                    .then((widget: any) => {
                        return (
                            widget
                                // Zoek het DOM-element met ID "global-header" welke we gaan
                                // gebruiken om de widget aan de koppelen. Zodra men de widget
                                // zijn "mount" gedrag triggered zal het DOM-element vervangen
                                // worden maar niet verwijderd. Het "umount" gedrag zal het
                                // originele DOM-element opnieuw herstellen.
                                .setMountElement(element)
                                // Geef instructie aan de widget instantie om "mount" gedrag
                                // uit te voeren.
                                .mount()
                        );
                    })
                    .catch((error: Error) => console.error(error));
            });
        }
    }

    /** Mounts the header if possible. */
    tryMountFooter(element: HTMLElement): void {
        if (this.#vl && this.#config.widgetRootUrl && this.#config.footerWidgetId) {
            let url = this.#config.widgetRootUrl;
            if (!url.endsWith('/')) url += '/';
            url += this.#config.footerWidgetId;

            this.#zone.runOutsideAngular(() => {
                this.#vl.widget.client
                    .bootstrap(url)
                    .then((widget: any) => {
                        return (
                            widget
                                // Zoek het DOM-element met ID "global-header" welke we gaan
                                // gebruiken om de widget aan de koppelen. Zodra men de widget
                                // zijn "mount" gedrag triggered zal het DOM-element vervangen
                                // worden maar niet verwijderd. Het "umount" gedrag zal het
                                // originele DOM-element opnieuw herstellen.
                                .setMountElement(element)
                                // Geef instructie aan de widget instantie om "mount" gedrag
                                // uit te voeren.
                                .mount()
                        );
                    })
                    .catch((error: Error) => console.error(error));
            });
        }
    }

    /** Super internal function to set the service points in the burgerprofiel. */
    _setContactServicePoints(servicePoints: _BaasBurgerprofielServicePoints): void {
        this.#headerComponent = servicePoints.headerComponent;
        // Store the contextual links with their icon information
        this.#contextualLinks = servicePoints.contextual;

        if (this.#contactExtension) {
            const realServicePoints = {
                displayMode: servicePoints.displayMode,
                groupLabel: servicePoints.groupLabel,
                contextual: this.#mapContactLinks(servicePoints.contextual)
            } as any;

            if (this.#originalServicePoints?.defaults) {
                // Take over the original defaults if not overruled
                realServicePoints.defaults = this.#originalServicePoints.defaults;
            }
            this.#contactExtension.setServicePoints(realServicePoints);
        } else {
            // Queue it for when the extension becomes available
            this.#pendingServicePoints = servicePoints;
        }
    }

    /** Super internal function to set applicative links in burgerprofiel. */
    _setApplicativeLinks(links: _BaasBurgerprofielApplicativeLink[]): void {
        if (this.#citizenProfileExtension) {
            this.#citizenProfileExtension
                .getMenu()
                .getGroup('application')
                .addMultiple(links as any[]);
        } else {
            // Queue it for when the extension becomes available
            this.#pendingApplicativeLinks = links;
        }
    }

    /**
     * Generic handler for custom actions triggered from burgerprofiel contact links
     * @param actionCode The code identifying which action to perform
     */
    _handleContactAction(actionCode: string): void {
        // Execute in Angular zone to ensure change detection works
        this.#zone.run(() => {
            const hook = this.#contactActionHooks?.find((h) => h.code === actionCode);

            if (hook) {
                hook.execute();
            } else {
                console.warn('No handler registered for contact action code:', actionCode);
            }
        });
    }

    async #tryPerformLogout(logoutRequest: any): Promise<void> {
        // Acknowledge the logout request to prevent the session extension from performing default
        // action due to response timeout (5 seconds).
        logoutRequest.acknowledge();

        switch (logoutRequest.getRequest().getReason()) {
            // Logout was requested by the user. This request should never be rejected in normal
            // circumstances.
            case 'user': {
                if (this.#sessionInterval !== undefined) {
                    window.clearInterval(this.#sessionInterval);
                    this.#sessionInterval = undefined;
                }
                // Accept the request for website logout.
                logoutRequest.accept();
                if (this.#auth?.logOff) this.#auth.logOff();
                break;
            }
            // Logout was requested as the citizen profile extension has detected inactivity from
            // the user.
            case 'inactivity': {
                // Reject the request for website logout.
                // NOTE LV: we don't have the concept of an expired session or inactivity
                logoutRequest.reject();
                break;
            }
            // Logout was requested because the citizen profile extension has detected an expired
            // session which prevents the user from accessing citizen profile without a step-up.
            // This could be the result of an application logout or external logout.
            case 'expired': {
                // Reject the request for website logout.
                // NOTE LV: we don't have the concept of an expired session or inactivity
                logoutRequest.reject();
                break;
            }
            default: {
                // Reject the request for website logout.
                // NOTE LV: we don't have the concept of an expired session or inactivity
                logoutRequest.reject();
                break;
            }
        }
    }

    async #tryRegisterSessionExtend(session: any): Promise<void> {
        /**
         * Event handler which extends a Citizen Profile session.
         */
        function activityEventHandler() {
            // Inform the Citizen Profile Session extension about activity.
            session.extend();
        }

        if (this.#config.useActivityTracking) {
            // NOTE this is the way according to burgerprofiel technical documentation
            // Build a list of event names which should be used for activity tracking.
            const eventNames = ['mousedown', 'mousemove', 'mousewheel', 'DOMMouseScroll', 'scroll', 'wheel', 'keydown', 'keypress', 'touchmove', 'touchstart'];
            // Iterate through the events names.
            for (const eventName of eventNames) {
                // Register our event handler given event name.
                window.addEventListener(eventName, activityEventHandler);
            }
        } else if (this.#config.sessionExtendTimeoutSeconds && this.#config.sessionExtendTimeoutSeconds > 0) {
            // NOTE the LV way: just try to keep the session permanently alive
            // buiten angular om change detection te voorkomen door interval
            // elke 5 minuten de sessie verlengen
            this.#sessionInterval = await this.#window.setIntervalOutsideAngular(() => activityEventHandler(), this.#config.sessionExtendTimeoutSeconds * 1000);
        }
    }

    #configureSession(session: any): void {
        // This variable is purely to indicate which values are allowed (true / false).
        const websiteHasAuthenticatedSession = this.#auth?.authenticated;
        // Inform the session extension about the current session state of the website.
        session.configure({
            active: websiteHasAuthenticatedSession,
            endpoints: {
                loginUrl: this.#config.loginUrl,
                loginRedirectUrl: this.#config.loginRedirectUrl,
                logoutUrl: this.#config.logoutUrl
            }
        });
    }

    /**
     * Maps our custom contact link format to the burgerprofiel widget format
     * @param links Array of custom contact links
     * @returns Array of links in the format expected by the burgerprofiel widget
     */
    #mapContactLinks(links: _BaasBurgerprofielContactLink[]): any[] {
        return links.map((link) => {
            const mappedLink: any = {
                type: 'link',
                label: link.label
            };

            if (link.behavior === 'regular') {
                // Regular navigation link
                mappedLink.href = link.href;
            } else if (link.behavior === 'action') {
                // Custom action link - execute JavaScript that calls our handler
                mappedLink.href = `javascript:window.barBurgerprofielEngine._handleContactAction("${link.action}")`;
            }

            return mappedLink;
        });
    }

    /**
     * Updates the icons in the contact panel based on the stored contextual links
     * This method is called by the mutation observer when the contact panel appears
     */
    #updateContactPanelIcons(): void {
        // Find all icon list items in the contact panel
        const iconItems = document.querySelectorAll('.vlw__contact-panel__icon-list__item__block__icon');

        if (iconItems.length === 0 || this.#contextualLinks.length === 0) {
            return;
        }

        // Iterate through the icon items and update their icons based on position
        iconItems.forEach((item, index) => {
            // Check if we have a corresponding contextual link at this position
            if (index < this.#contextualLinks.length) {
                const link = this.#contextualLinks[index];

                // Only update if an icon is specified
                if (link.icon) {
                    // Find the SVG use element within this item
                    const useElement = item.querySelector('.vlw__contact-panel__icon-list__item__block__icon__svg use');

                    if (useElement) {
                        // Update the href attribute to reference the new icon
                        useElement.setAttribute('href', `#icon-${link.icon}`);
                    }
                }
            }
        });
    }

    #customProcessing(): void {
        if (!this.#config.impersonationOverrideEnabled || !this.#state?.basiscontext?.impersonate) return;
        // here we inject our own widget using the css of burgerprofiel
        const element = document.querySelector('.vlw__primary-bar__plugins__item.vlw__profile');
        if (element) {
            // Create an instance of the component
            const componentRef = createComponent(BarImpersonationWidgetComponent, {
                environmentInjector: this.#environmentInjector,
                elementInjector: this.#injector
            });
            element.innerHTML = '';
            // Attach the component to the container element
            element.appendChild(componentRef.location.nativeElement);
            // Trigger change detection to update the component
            componentRef.changeDetectorRef.detectChanges();
        }
    }

    // NOTE a href="#" appears which causes a real navigation in some cases
    #fixUpContactToggle(): void {
        const contactToggle = document.querySelector('a.vlw__contact__toggle[href="#"]');
        if (contactToggle && contactToggle.hasAttribute('href')) {
            contactToggle.removeAttribute('href');
        }
    }
}
